// @ts-nocheck
/**
   * Careers EOI Form Component
   *
   * Manages the Expression of Interest form modal for job applications.
   * Handles modal open/close, focus management, form validation, and submission.
   * Integrates with Powerful Form Builder when configured.
   *
   * @typedef {Object} CareerEOIFormRefs
   * @property {HTMLDialogElement|null} modal - The modal dialog element
   * @property {HTMLButtonElement|null} closeButton - Close button
   *
   * @extends {HTMLElement}
   */
class CareerEOIForm extends HTMLElement {
  requiredRefs = [
    'diaglog-component',
    'closeButton'
  ];

  /**
   * Get refs object for accessing DOM elements
   * @returns {CareerEOIFormRefs}
   */
  get refs() {
    return {
      modal: this.querySelector('[ref="diaglog-component"]'),
      closeButton: this.querySelector('[ref="closeButton"]')
    };
  }

  /** @type {Object|null} */
  #currentLocation = null;

  /** @type {Object|null} */
  #careersData = null;

  connectedCallback() {
    // Listen for open form events
    document.addEventListener('careers:open-form', this.#handleOpen.bind(this));

    // Handle escape key
    document.addEventListener('keydown', this.#handleEscape.bind(this));

    // Load careers data
    this.#loadCareersData();
  }

  disconnectedCallback() {
    document.removeEventListener('careers:open-form', this.#handleOpen.bind(this));
    document.removeEventListener('keydown', this.#handleEscape.bind(this));
  }

  /**
   * Load careers data from the JSON script tag
   */
  #loadCareersData() {
    const dataScript = document.getElementById('careers-data');
    if (dataScript) {
      try {
        this.#careersData = JSON.parse(dataScript.textContent);
      } catch (error) {
        console.error('Error parsing careers data:', error);
      }
    }
  }


  /**
   * Handle open form event
   * @param {CustomEvent} event - Custom event with location data
   */
  #handleOpen = (event) => {
    // Check if location has changed and hide messages if so
    if (this.#currentLocation &&
      this.#currentLocation.locationName !== event.detail.locationName) {
      this.#hideMessages();
    }

    this.#currentLocation = event.detail;
    this.#populateModal(event.detail);
    this.#openModal();
  };

  /**
   * Populate modal with location data
   * @param {Object} location - Location data
   * @param {string} location.id - Location ID
   * @param {string} location.name - Location name
   * @param {string} location.city - City
   * @param {string} location.province - Province/State
   */
  #populateModal(location) {
    // Find and update title with location name
    const titleElement = this.querySelector('.title');
    if (titleElement) {
      titleElement.textContent = location.locationName;
    }

    // Find and update description with address (City, Province/State)
    const descriptionElement = this.querySelector('.description');
    if (descriptionElement) {
      descriptionElement.textContent = location.locationAddress;
    }

    // Find and update position select dropdown options based on positions from event
    const positionSelect = this.querySelector('.position-select-dropdown select');
    if (positionSelect && location.positions) {
      const selectPositionText = this.getAttribute('data-select-position-text') || 'Select Position';
      positionSelect.innerHTML = `<option value="" disabled selected>${selectPositionText}</option>`;

      for (const position of location.positions) {
        const option = document.createElement('option');
        option.value = position;
        option.textContent = position;
        positionSelect.appendChild(option);
      }
    }

    // Find label[data-label=store-email] and update next sibling hidden input
    const storeEmailLabel = this.querySelector('label[data-label="store-email"]');
    if (storeEmailLabel && location.email) {
      const hiddenInput = storeEmailLabel.nextElementSibling;
      if (hiddenInput && hiddenInput.tagName === 'INPUT' && hiddenInput.type === 'hidden') {
        hiddenInput.value = location.email;
      }
    }
  }


  /**
   * Open the modal
   */
  #openModal() {
    const { modal, closeButton } = this.refs;
    if (!modal) return;

    modal.showDialog();

    // Trap focus
    this.#trapFocus();
  }

  /**
   * Handle close button click
   */
  handleClose() {
    this.#closeModal();
  }

  /**
   * Handle escape key press
   * @param {KeyboardEvent} event - Keyboard event
   */
  #handleEscape = (event) => {
    const { modal } = this.refs;
    if (event.key === 'Escape' && modal && modal.open) {
      this.#closeModal();
    }
  };

  /**
   * Close the modal
   */
  #closeModal() {
    const { modal } = this.refs;
    if (!modal) return;

    modal.close();
    document.body.style.overflow = '';

    // Reset form
    const form = this.querySelector('form');
    if (form) {
      form.reset();
    }
    this.#clearErrors();
    this.#hideMessages();
  }

  /**
   * Trap focus within modal
   */
  #trapFocus() {
    const { modal } = this.refs;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    /** @param {KeyboardEvent} event */
    const handleTabKey = (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          if (lastElement && 'focus' in lastElement && typeof lastElement.focus === 'function') {
            lastElement.focus();
          }
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          if (firstElement && 'focus' in firstElement && typeof firstElement.focus === 'function') {
            firstElement.focus();
          }
        }
      }
    };

    // Remove existing listener if any
    modal.removeEventListener('keydown', handleTabKey);
    modal.addEventListener('keydown', handleTabKey);
  }




  /**
   * Submit custom form
   * @param {FormData} formData - Form data
   */
  async #submitCustomForm(formData) {
    const response = await fetch('/apps/careers/submit', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit application');
    }

    const result = await response.json();
    this.#showSuccess(result);

    // Close modal after delay
    setTimeout(() => {
      this.#closeModal();
    }, 2000);
  }

  /**
   * Validate form fields
   * @returns {boolean} True if form is valid
   */
  #validateForm() {
    let isValid = true;
    this.#clearErrors();

    const form = this.querySelector('form');
    if (!form) return false;

    const requiredFields = form.querySelectorAll('[required]');

    for (const field of requiredFields) {
      if (!(field instanceof HTMLInputElement) && !(field instanceof HTMLSelectElement)) continue;
      if (!field.value || !field.value.trim()) {
        const requiredMessage = this.getAttribute('data-required-message') || 'This field is required';
        this.#showError(field, requiredMessage);
        isValid = false;
      }
    }

    // Validate email format
    const emailField = form.querySelector('input[type="email"]');
    if (emailField && emailField.value && !this.#isValidEmail(emailField.value)) {
      const emailMessage = this.getAttribute('data-invalid-email-message') || 'Please enter a valid email address';
      this.#showError(emailField, emailMessage);
      isValid = false;
    }

    return isValid;
  }

  /**
   * Check if email is valid
   * @param {string} email - Email address
   * @returns {boolean} True if valid
   */
  #isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Show error for a field
   * @param {HTMLElement} field - Input field
   * @param {string} message - Error message
   */
  #showError(field, message) {
    const errorId = field.id.replace(/^(.+)-/, '$1Error-');
    const errorElement = document.getElementById(errorId);

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('careers-eoi-form__error--visible');
    }

    field.setAttribute('aria-invalid', 'true');
    field.setAttribute('aria-describedby', errorId);
  }

  /**
   * Clear error for a field
   * @param {HTMLElement} field - Input field
   */
  #clearError(field) {
    const errorId = field.id.replace(/^(.+)-/, '$1Error-');
    const errorElement = document.getElementById(errorId);

    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('careers-eoi-form__error--visible');
    }

    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
  }

  /**
   * Clear all errors
   */
  #clearErrors() {
    const form = this.querySelector('form');
    if (!form) return;

    const errorElements = form.querySelectorAll('.careers-eoi-form__error');

    for (const element of errorElements) {
      element.textContent = '';
      element.classList.remove('careers-eoi-form__error--visible');
    }

    const invalidFields = form.querySelectorAll('[aria-invalid="true"]');

    for (const field of invalidFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }
  }

  /**
   * Show success message
   * @param {Object} result - Response data
   * @param {string} result.location_name - Location name
   */
  #showSuccess(result) {
    const successMessage = this.querySelector('.careers-eoi-form__success');

    if (!successMessage) return;

    const message = this.getAttribute('data-success-message') || this.getAttribute('data-default-success-message') || 'Thank you! Your application has been sent to {location_name}';
    successMessage.textContent = message.replace('{location_name}', result.location_name);
    successMessage.classList.add('careers-eoi-form__success--visible');
  }

  /**
   * Show general error message
   * @param {string} message - Error message
   */
  #showGeneralError(message) {
    const errorGeneral = this.querySelector('.careers-eoi-form__error-general');

    if (!errorGeneral) return;

    errorGeneral.textContent = message;
    errorGeneral.classList.add('careers-eoi-form__error-general--visible');
  }

  /**
   * Hide all messages
   */
  #hideMessages() {
    // Hide .message.error and .message.success elements
    const messageError = this.querySelector('.message.error');
    const messageSuccess = this.querySelector('.message.success');

    if (messageError && this.hasAttribute('data-reset-messages')) {
      messageError.style.display = 'none';
    }

    if (messageSuccess) {
      messageSuccess.style.display = 'none';
    }
  }

  /**
   * Set loading state
   * @param {boolean} loading - Whether form is submitting
   */
  #setLoadingState(loading) {
    const submitButton = this.querySelector('button[type="submit"]');
    if (!submitButton) return;

    submitButton.disabled = loading;
    const loadingText = this.getAttribute('data-loading-text') || 'Submitting...';
    const submitText = this.getAttribute('data-submit-button-text') || this.getAttribute('data-default-submit-text') || 'Submit Application';
    submitButton.textContent = loading ? loadingText : submitText;
  }
}

if (!customElements.get('career-eoi-form')) {
  customElements.define('career-eoi-form', CareerEOIForm);
}
